/**
 * Lock screen for PIN and biometric-based app unlock.
 *
 * Shown when the app detects an existing encrypted mnemonic (returning user).
 * Supports biometric unlock (if enrolled) with PIN as fallback.
 * Shows "Forgot PIN?" link navigating to mnemonic recovery.
 * Shows "open in another tab" when tab coordinator signals session claimed.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module LockScreen
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { PinEntry } from '@/components/ui/pin-entry';
import { decryptMnemonic, PinError } from '@/lib/crypto-encrypt';
import { createPlatformServices } from '@/lib/platform/index';
import { useMnemonic } from '@/contexts/MnemonicProvider';
import { useAuth } from '@/contexts/AuthProvider';
import { useBiometric } from '@/features/auth/hooks/useBiometric';
import { Button } from '@/components/ui/button';

/** SecureStorage key for encrypted mnemonic blob. */
const ENCRYPTED_MNEMONIC_KEY = 'encrypted_mnemonic';

/** Props for LockScreen. */
interface LockScreenProps {
  /** Callback when user taps "Forgot PIN?". If provided, used instead of navigation. */
  readonly onForgotPin?: () => void;
}

/**
 * Lock screen component with biometric + PIN unlock.
 *
 * If biometric is enabled, auto-triggers biometric prompt on load.
 * Falls back to PIN entry. Shows "Forgot PIN?" link for recovery.
 *
 * Tier: All tiers (core infrastructure).
 */
export function LockScreen({ onForgotPin }: LockScreenProps = {}): React.ReactElement {
  const [encryptedBlob, setEncryptedBlob] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingBlob, setLoadingBlob] = useState(true);
  const [tabClaimed, setTabClaimed] = useState(false);
  const { unlock: mnemonicUnlock, isUnlocked } = useMnemonic();
  const { unlock: authUnlock } = useAuth();
  const biometric = useBiometric();

  // Track previous isUnlocked value to detect transition from true -> false
  const wasUnlockedRef = useRef(isUnlocked);

  // Detect when another tab claims the mnemonic session.
  // MnemonicProvider sets mnemonic to null when another tab claims session,
  // causing isUnlocked to transition from true to false. We detect this
  // transition and show the "open in another tab" message.
  useEffect(() => {
    if (wasUnlockedRef.current && !isUnlocked) {
      setTabClaimed(true);
    }
    wasUnlockedRef.current = isUnlocked;
  }, [isUnlocked]);

  // Load encrypted blob from SecureStorage on mount
  useEffect(() => {
    let cancelled = false;

    async function loadBlob(): Promise<void> {
      try {
        const services = await createPlatformServices();
        const blob = await services.secureStorage.retrieve(ENCRYPTED_MNEMONIC_KEY);
        if (!cancelled) {
          setEncryptedBlob(blob);
          setLoadingBlob(false);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load wallet data');
          setLoadingBlob(false);
        }
      }
    }

    void loadBlob();

    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-trigger biometric prompt when biometric is enabled and blob is loaded
  useEffect(() => {
    if (loadingBlob || !encryptedBlob || !biometric.isEnabled) return;

    let cancelled = false;

    async function attemptBiometric(): Promise<void> {
      const success = await biometric.unlockWithBiometric();
      if (!cancelled && success) {
        authUnlock();
      }
    }

    void attemptBiometric();

    return () => {
      cancelled = true;
    };
  }, [loadingBlob, encryptedBlob, biometric.isEnabled, biometric.unlockWithBiometric, authUnlock]);

  const handlePinComplete = useCallback(
    async (pin: string): Promise<void> => {
      if (!encryptedBlob) {
        setError('No wallet data found');
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        const mnemonic = await decryptMnemonic(encryptedBlob, pin);
        mnemonicUnlock(mnemonic);
        authUnlock();
      } catch (err) {
        if (err instanceof PinError) {
          setError('Incorrect PIN');
        } else {
          setError('Failed to unlock wallet');
        }
      } finally {
        setLoading(false);
      }
    },
    [encryptedBlob, mnemonicUnlock, authUnlock],
  );

  const handleBiometricTap = useCallback(async (): Promise<void> => {
    setError(undefined);
    const success = await biometric.unlockWithBiometric();
    if (success) {
      authUnlock();
    } else if (biometric.error) {
      setError('Biometric failed. Enter your PIN.');
    }
  }, [biometric, authUnlock]);

  if (loadingBlob) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
            Loading...
          </p>
        </div>
      </main>
    );
  }

  if (tabClaimed) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-background px-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-lg font-semibold text-foreground">Wallet is open in another tab</p>
          <p className="text-sm text-muted-foreground">
            Your wallet can only be active in one tab at a time. Close this tab or unlock again to
            take over.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              setTabClaimed(false);
            }}
          >
            Unlock here instead
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-6">
      <div className="flex flex-col items-center gap-6">
        <PinEntry
          title="Enter your PIN"
          onComplete={(pin) => void handlePinComplete(pin)}
          error={error}
          loading={loading}
        />

        {/* Biometric button */}
        {biometric.isEnabled ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void handleBiometricTap()}
            aria-label="Unlock with biometric"
            disabled={biometric.isLoading}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
              aria-hidden="true"
            >
              <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
              <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
              <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
              <path d="M2 12a10 10 0 0 1 18-6" />
              <path d="M2 16h.01" />
              <path d="M21.8 16c.2-2 .131-5.354 0-6" />
              <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
              <path d="M8.65 22c.21-.66.45-1.32.57-2" />
              <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
            </svg>
          </Button>
        ) : null}

        {/* Forgot PIN link */}
        {onForgotPin ? (
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            onClick={onForgotPin}
          >
            Forgot PIN?
          </button>
        ) : null}
      </div>
    </main>
  );
}
